import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import { ArrowRight, DollarSign, TrendingUp, Clock, Users, LineChart, Layers } from "lucide-react";

const PUBLISHED = "2026-07-07";
const UPDATED = "2026-07-07";
const CANONICAL = "https://cargonova.ai/blog/roi-of-ai-agents-freight-forwarding";
const CANONICAL_ZH = "https://cargonova.ai/zh/blog/roi-of-ai-agents-freight-forwarding";
const CANONICAL_TH = "https://cargonova.ai/th/blog/roi-of-ai-agents-freight-forwarding";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "The ROI of AI Agents in Freight Forwarding",
  description:
    "A financial deep-dive on shifting from manual data entry and RPA scripts to AI agents that own rates, booking, and documentation in AI freight forwarding.",
  datePublished: PUBLISHED,
  dateModified: UPDATED,
  author: { "@type": "Organization", name: "CargoNova" },
  publisher: { "@id": "https://cargonova.ai/#organization" },
  mainEntityOfPage: CANONICAL,
  inLanguage: "en",
  about: [
    "AI freight forwarding",
    "AI agents",
    "Return on investment",
    "Logistics automation",
    "RPA",
  ],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cargonova.ai/" },
    { "@type": "ListItem", position: 2, name: "Blog", item: "https://cargonova.ai/blog" },
    {
      "@type": "ListItem",
      position: 3,
      name: "The ROI of AI Agents in Freight Forwarding",
      item: CANONICAL,
    },
  ],
};

const contentEn = {
  eyebrow: "ROI Guide · AI Freight Forwarding",
  title: "The ROI of AI Agents in Freight Forwarding",
  lede:
    "Every forwarder wants to know the same thing before signing an automation contract: when does this pay back? This guide breaks down the economics of shifting from manual data entry — and brittle RPA scripts — to AI agents that own quoting, booking, and documentation end-to-end.",
  meta: "9 min read · Updated July 7, 2026",
  sections: [
    {
      heading: "Where the cost really sits in a modern forwarder",
      body: [
        "For most mid-size AI freight forwarding operators, direct labor on repeat operational work — quoting, booking, document handling, customs filing, invoice reconciliation — is the single largest controllable cost line. A typical desk operator handles 60 to 120 shipments per month; a rate desk clerk processes hundreds of rate requests. Every one of those shipments touches five to fifteen documents and a handful of external portals.",
        "The economic question is not whether to automate. It is which layer of automation gives you a durable cost curve. Manual work scales linearly with volume. RPA scripts break with every portal change. AI agents flatten the labor curve and compound as data grows.",
      ],
    },
    {
      heading: "Manual, RPA, and AI agents — what each layer actually costs",
      table: [
        ["Dimension", "Manual desk", "RPA scripts", "AI agents"],
        ["Cost per shipment", "Highest", "Medium", "Lowest at scale"],
        ["Setup time", "None", "6–12 weeks per workflow", "2–6 weeks per role"],
        ["Maintenance", "N/A", "Ongoing, per-portal", "Model-driven, low"],
        ["Scales with volume", "Linear headcount", "Linear script count", "Sub-linear"],
        ["Breaks on portal changes", "No", "Yes, often", "Rare, self-adapts"],
        ["ROI curve", "Flat", "Plateaus after year 1", "Compounds with data"],
      ],
    },
    {
      heading: "A back-of-envelope ROI model",
      body: [
        "Take a mid-size forwarder with 12 operational FTEs (rate desk, booking, documentation, customs) at a fully-loaded cost of USD 4,500 per month per FTE — roughly USD 648,000 per year in operational labor. Assume AI agents can absorb 60–70% of the repeat workload across rates, booking, docs, and settlement, with humans supervising exceptions.",
        "That reallocation typically frees 6–8 FTEs of capacity. Some forwarders take that as headcount savings; most redeploy that capacity into growth — more lanes, more customers, faster response times. Either way, the recurring cost of the AI agent platform is a fraction of the freed labor, and the payback window in practice sits between 4 and 9 months.",
        "The compounding piece is often underestimated. Each processed shipment improves rate coverage, document templates, and exception patterns. Year two typically delivers another 15–25% efficiency improvement on top of the year-one baseline — a curve you never get from RPA.",
      ],
    },
    {
      heading: "Direct vs. indirect ROI — both matter",
      body: [
        "Direct ROI is easy to defend to a CFO: reduced cost per shipment, reduced overtime, fewer temp staff during peaks. These are the numbers that get quoted in board decks.",
        "Indirect ROI is where competitive advantage compounds: quotes go out in minutes instead of hours, so win rates climb. Documentation errors drop, so demurrage and rework charges shrink. Faster settlement improves cash conversion. Operations teams stop firefighting and start building. None of these show up on the RPA business case — but they show up on the P&L.",
      ],
    },
    {
      heading: "Where AI agents fall short of expectations — and how to avoid it",
      body: [
        "AI agents underperform when they are deployed as isolated point tools without ownership of a full workflow. A chatbot answering rate questions but not writing quotes into the TMS produces marginal ROI. A document extractor that feeds a spreadsheet no one reads produces none.",
        "The forwarders who capture full ROI treat each AI agent as an actual role: rate optimization, quoting, booking, documentation, customs, settlement. Each role has KPIs, an exception queue, and a human supervisor. That is the design center of the CargoNova platform and the reason its customers see payback in months, not years.",
      ],
    },
    {
      heading: "A practical ROI checklist before you buy",
      body: [
        "Ask any AI freight forwarding vendor three questions before you commit. First: which full roles will your agents own — not which tasks, but which end-to-end roles? Second: what happens on day 90 when a carrier redesigns its portal or introduces a new surcharge code? Third: how does the platform expose exceptions and reasoning so operators can trust the automation?",
        "If a vendor cannot answer those cleanly, the ROI model is theoretical. If they can, the payback math is straightforward — and typically far better than any RPA business case you have run before.",
      ],
    },
  ],
  ctaTitle: "Model your own ROI with CargoNova",
  ctaBody:
    "Walk through your volumes, workflows, and current tooling with our team — we will co-build the ROI model for your operation.",
  ctaButton: "Book an ROI walkthrough",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "Back to home",
};

const contentZh = {
  eyebrow: "ROI 深度指南 · AI Native 国际货代",
  title: "AI Agent 在货代场景的 ROI 深度解读",
  lede:
    "任何一家货代在签下自动化合同之前都想弄清同一件事：多久能回本？本篇拆解从手工录入、脆弱 RPA 脚本切换到 AI Agent 端到端接管报价、订舱与单证时的经济账。",
  meta: "阅读约 9 分钟 · 2026 年 7 月 7 日更新",
  sections: [
    {
      heading: "现代货代的成本到底压在哪里",
      body: [
        "对大多数中型 AI Native 货代而言，重复性操作岗位的直接人工成本——报价、订舱、单证、清关、对账——就是最大的一块可控成本。一个操作坐席通常每月处理 60–120 票货，一位运价坐席每月处理数百条询价，每一票背后是 5–15 份单证和若干外部门户系统。",
        "所以真正要回答的经济问题不是要不要自动化，而是选择哪一层自动化能得到可持续的成本曲线。人工与业务量线性挂钩，RPA 每次门户改版就掉链子，AI Agent 把人工成本曲线压平，并随数据规模持续复利。",
      ],
    },
    {
      heading: "手工、RPA、AI Agent 到底成本几何",
      table: [
        ["维度", "手工坐席", "RPA 脚本", "AI Agent"],
        ["单票成本", "最高", "中等", "规模化后最低"],
        ["上线时长", "无", "单流程 6–12 周", "单角色 2–6 周"],
        ["维护成本", "不适用", "持续，按门户维护", "低，由模型驱动"],
        ["随业务量增长", "人头线性增长", "脚本数量线性增长", "亚线性增长"],
        ["门户变更受影响", "否", "经常", "极少，自适应"],
        ["ROI 曲线", "持平", "第一年后趋平", "随数据复利"],
      ],
    },
    {
      heading: "一份粗算的 ROI 模型",
      body: [
        "假设一家中型货代有 12 名操作 FTE（运价、订舱、单证、关务），全成本每人每月 USD 4,500，一年操作人工约 USD 648,000。若 AI Agent 能够消化运价、订舱、单证与结算 60–70% 的重复工作量，人工只负责异常处理与决策。",
        "这样通常可释放 6–8 名 FTE 的产能。部分货代直接算成人头节约；更多货代则把这部分产能投入到增长——开新航线、拓客户、缩短响应时间。无论哪种策略，AI Agent 平台的年费只占释放人工成本的一小部分，实际回收期通常落在 4–9 个月之间。",
        "更容易被低估的是复利效应。每一票被处理过的业务都会改善运价覆盖、单证模板与异常样本；第二年通常还能在第一年基础上再提升 15–25% 的效率——这条曲线是 RPA 拿不到的。",
      ],
    },
    {
      heading: "直接 ROI 与间接 ROI，同样重要",
      body: [
        "直接 ROI 好向 CFO 交代：单票成本下降、加班减少、旺季临时工减少——都是能直接写进董事会材料的数字。",
        "间接 ROI 才是竞争壁垒复利的地方：报价从小时级压到分钟级，成单率上升；单证差错下降，滞港费与返工损失下降；结算加快，现金周转改善；运营团队从救火切换到搭建能力。这些都进不了 RPA 商业案例的 Excel，却真真切切进 P&L。",
      ],
    },
    {
      heading: "AI Agent 落地不达预期的常见原因",
      body: [
        "AI Agent 表现差，通常是因为被当作孤立点工具部署、没有拥有完整流程。一个只回答运价问题、不把报价写回 TMS 的聊天机器人，只能拿到边际收益；一个把提取的字段扔进无人打开的表格的抽取器，几乎没有收益。",
        "真正拿到完整 ROI 的货代，都把每一个 AI Agent 当作真正的岗位来看：运价优化、报价、订舱、单证、关务、结算。每个岗位有 KPI、异常队列和人工监督者。这正是 CargoNova 平台的设计原点，也是客户能在数月内回本的原因。",
      ],
    },
    {
      heading: "采购前的一份 ROI 检查清单",
      body: [
        "在签合同前，至少问 AI Native 货代平台厂商三个问题。第一：你们的 AI Agent 到底拥有哪些完整岗位——不是任务清单，而是端到端角色？第二：上线第 90 天，船司改版门户、启用新附加费代码时会发生什么？第三：平台如何暴露异常与推理过程，让操作团队能真正信任自动化？",
        "如果厂商答不清楚，ROI 模型就是空中楼阁；如果答得出来，回本测算就非常直接，而且几乎一定比你之前跑过的 RPA 商业案例更漂亮。",
      ],
    },
  ],
  ctaTitle: "和 CargoNova 一起测算你的 ROI",
  ctaBody:
    "把你的业务量、流程和现有工具带过来，我们和你一起搭建属于你自己业务的 ROI 模型。",
  ctaButton: "预约 ROI 深度沟通",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "返回首页",
};

const highlightsEn = [
  { icon: DollarSign, label: "4–9 month payback" },
  { icon: TrendingUp, label: "ROI compounds year over year" },
  { icon: Clock, label: "Weeks to deploy, not quarters" },
  { icon: Users, label: "Redeploy staff into growth" },
  { icon: LineChart, label: "Sub-linear cost per shipment" },
  { icon: Layers, label: "Role-level, not task-level" },
];

const highlightsZh = [
  { icon: DollarSign, label: "4–9 个月回本" },
  { icon: TrendingUp, label: "ROI 逐年复利" },
  { icon: Clock, label: "以周为单位上线" },
  { icon: Users, label: "释放人力投向增长" },
  { icon: LineChart, label: "单票成本亚线性" },
  { icon: Layers, label: "岗位级而非任务级" },
];

const BlogRoiAiAgents = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const c = isZh ? contentZh : contentEn;
  const highlights = isZh ? highlightsZh : highlightsEn;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={
          isZh
            ? "AI Agent 货代 ROI 深度指南 | CargoNova"
            : "ROI of AI Agents in Freight Forwarding | CargoNova"
        }
        description={
          isZh
            ? "拆解 AI Agent 在国际货代场景的经济账：与手工、RPA 的单票成本对比、回本周期、直接与间接 ROI，以及采购前的检查清单。"
            : "The financial case for AI agents in AI freight forwarding: cost per shipment vs. manual and RPA, payback window, direct and indirect ROI, and a buyer's checklist."
        }
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-4xl pt-10 md:pt-16 pb-8 md:pb-12">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.15em] text-secondary mb-4">
            {c.eyebrow}
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.2] bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent">
            {c.title}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
            {c.lede}
          </p>
          <p className="text-xs text-muted-foreground/80">{c.meta}</p>
        </section>

        {/* Highlights */}
        <section className="relative container mx-auto px-4 max-w-5xl pb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {highlights.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-2xl bg-card/80 backdrop-blur ring-1 ring-border/50 px-4 py-3 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-foreground">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Article */}
        <article className="relative container mx-auto px-4 max-w-3xl py-8 md:py-12">
          {c.sections.map((section, idx) => (
            <section key={idx} className="mb-10 md:mb-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 leading-snug">
                {section.heading}
              </h2>

              {"body" in section &&
                section.body?.map((p, i) => (
                  <p
                    key={i}
                    className="text-[15px] md:text-base leading-relaxed text-muted-foreground mb-4"
                  >
                    {p}
                  </p>
                ))}

              {"table" in section && section.table && (
                <div className="overflow-x-auto rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary/10 text-foreground">
                        {section.table[0]!.map((h) => (
                          <th
                            key={h}
                            className="text-left font-semibold px-4 py-3 border-b border-border/50"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.slice(1).map((row, r) => (
                        <tr key={r} className="odd:bg-background/50">
                          {row.map((cell, cIdx) => (
                            <td
                              key={cIdx}
                              className={`px-4 py-3 border-b border-border/40 align-top ${
                                cIdx === 0
                                  ? "font-medium text-foreground"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">{c.ctaBody}</p>
            <a
              href={c.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
            >
              {c.ctaButton}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-8 text-sm">
            <Link
              to={isZh ? "/zh" : "/"}
              className="text-secondary hover:underline inline-flex items-center gap-1"
            >
              ← {c.backLabel}
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogRoiAiAgents;
